import { Close } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  SwipeableDrawer,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { ReactNode } from "react";

interface MobileBottomSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Largura máxima do sheet (web continua Dialog). */
  maxWidth?: "sm" | "md" | "lg";
}

/** Modal responsivo: bottom sheet vertical no mobile, Dialog no web. */
export function MobileBottomSheet({
  open,
  onClose,
  title,
  children,
  maxWidth = "md",
}: MobileBottomSheetProps) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));

  if (!isMobileLayout) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth={maxWidth}
        PaperProps={{
          sx: { borderRadius: 2.5, p: 0.5 },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          {title}
          <IconButton aria-label="Fechar" onClick={onClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{children}</DialogContent>
      </Dialog>
    );
  }

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => undefined}
      disableSwipeToOpen
      swipeAreaWidth={0}
      ModalProps={{
        keepMounted: false,
        sx: { zIndex: theme.zIndex.modal + 2 },
        BackdropProps: {
          sx: { bgcolor: alpha(theme.palette.common.black, 0.45) },
        },
      }}
      PaperProps={{
        sx: {
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: "20px 20px 0 0",
          maxHeight: "92dvh",
          bgcolor: "background.paper",
          boxShadow: "0 -10px 40px rgba(0, 0, 0, 0.14)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: 99,
            bgcolor: "divider",
          }}
        />
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, pb: 1, pt: 0.25 }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ minWidth: 0 }}>
          {title}
        </Typography>
        <IconButton aria-label="Fechar" onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Stack>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          px: 2,
          pb: 2,
        }}
      >
        {children}
      </Box>
    </SwipeableDrawer>
  );
}
